import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    PieChart, Pie, Cell, Legend,
} from "recharts";
import { PageHeader, StatCard } from "@/components/platform/platformUi";

const PIE_COLORS = ["#4A0E17", "#C9A227", "#D9468F", "#3B82C4", "#2D9B6E", "#C84B31"];

export default function PlatformAnalytics() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/platform/analytics")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Could not load analytics"));
    }, []);

    const subPie = data
        ? Object.entries(data.subscription_status || {}).map(([name, value]) => ({ name, value }))
        : [];

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-analytics-page">
            <PageHeader
                eyebrow="Analytics"
                title="Platform analytics"
                subtitle="Signup trends, plan distribution, and salon usage across the platform"
            />

            {data && (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard label="Total salons" value={data.totals?.salons ?? 0} sub={`${data.totals?.suspended ?? 0} suspended`} />
                        <StatCard label="Trial active" value={data.trial_funnel?.trial_active ?? 0} />
                        <StatCard label="Trial expired" value={data.trial_funnel?.trial_expired ?? 0} />
                        <StatCard label="Paid active" value={data.trial_funnel?.paid_active ?? 0} accent="#DCEEDC" iconColor="#2D9B6E" />
                        <StatCard label="Appointments (30d)" value={data.usage_30d?.appointments ?? 0} accent="#E0EDFA" iconColor="#3B82C4" />
                        <StatCard label="Invoices (30d)" value={data.usage_30d?.invoices ?? 0} accent="#FBE4EF" iconColor="#D9468F" />
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                        <section className="rounded-xl border border-[#E8E4DE] bg-white p-5">
                            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">Signups (6 months)</p>
                            <div className="mt-4 h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.signups_by_month || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" />
                                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#C9A227" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </section>

                        <section className="rounded-xl border border-[#E8E4DE] bg-white p-5">
                            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">Subscription status</p>
                            <div className="mt-4 h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={subPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                            {subPie.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </section>
                    </div>

                    <section className="rounded-xl border border-[#E8E4DE] bg-white p-5">
                        <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">Salons by plan</p>
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase text-[#8A8178]">
                                        <th className="py-2 pr-4">Plan</th>
                                        <th className="py-2">Salons</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.tenants_by_plan || []).map((row) => (
                                        <tr key={row.plan} className="border-b border-[#E8E4DE]/40">
                                            <td className="py-2.5 pr-4 font-semibold text-[#4A0E17]">{row.plan}</td>
                                            <td className="py-2.5 font-medium text-[#3D2817]">{row.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
